import { useEffect, useState, useRef, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { HelmetProvider } from "react-helmet-async";

// Handle chunk load errors from stale deployments — auto-refresh once
const lazyWithRetry = (importFn) => lazy(() =>
  importFn().catch(() => {
    const hasRefreshed = sessionStorage.getItem('chunk_retry');
    if (!hasRefreshed) {
      sessionStorage.setItem('chunk_retry', '1');
      window.location.reload();
    }
    return importFn();
  })
);

// Critical pages - loaded immediately (only homepage needs to be eager)
import HomePage from "@/pages/HomePage";

// Lazy loaded pages - code split for better performance
const LoginPage = lazyWithRetry(() => import("@/pages/LoginPage"));
const SearchPage = lazyWithRetry(() => import("@/pages/SearchPage"));
const ListingPage = lazyWithRetry(() => import("@/pages/ListingPage"));

// Lazy loaded pages - code split for better performance
const ForgotPasswordPage = lazyWithRetry(() => import("@/pages/ForgotPasswordPage"));
const ResetPasswordPage = lazyWithRetry(() => import("@/pages/ResetPasswordPage"));
const CampDashboard = lazyWithRetry(() => import("@/pages/CampDashboard"));
const CampCalendar = lazyWithRetry(() => import("@/pages/CampCalendar"));
const ScheduleDesigner = lazyWithRetry(() => import("@/pages/ScheduleDesigner"));
const TeamsPage = lazyWithRetry(() => import("@/pages/TeamsPage"));
const RemindersPage = lazyWithRetry(() => import("@/pages/RemindersPage"));
const KanbanPage = lazyWithRetry(() => import("@/pages/KanbanPage"));
const NotificationSettingsPage = lazyWithRetry(() => import("@/pages/NotificationSettingsPage"));
const VendorDashboard = lazyWithRetry(() => import("@/pages/VendorDashboard"));
const CreateListing = lazyWithRetry(() => import("@/pages/CreateListing"));
const AccountSettings = lazyWithRetry(() => import("@/pages/AccountSettings"));
const AdminDashboard = lazyWithRetry(() => import("@/pages/admin/AdminDashboard"));
const PricingPage = lazyWithRetry(() => import("@/pages/PricingPage"));
const PaymentSuccess = lazyWithRetry(() => import("@/pages/PaymentSuccess"));
const PaymentCancel = lazyWithRetry(() => import("@/pages/PaymentCancel"));
const TermsOfServicePage = lazyWithRetry(() => import("@/pages/TermsOfServicePage"));
const PrivacyPolicyPage = lazyWithRetry(() => import("@/pages/PrivacyPolicyPage"));
const ForVendorsPage = lazyWithRetry(() => import("@/pages/ForVendorsPage"));
const ForDirectorsPage = lazyWithRetry(() => import("@/pages/ForDirectorsPage"));
const UnsubscribePage = lazyWithRetry(() => import("@/pages/UnsubscribePage"));
const TripBoard = lazyWithRetry(() => import("@/pages/TripBoard"));
const PostTrip = lazyWithRetry(() => import("@/pages/PostTrip"));
const RejectListingPage = lazyWithRetry(() => import("@/pages/RejectListingPage"));
const ApprovalSuccessPage = lazyWithRetry(() => import("@/pages/ApprovalSuccessPage"));
const DealsPage = lazyWithRetry(() => import("@/pages/DealsPage"));
const VendorDeals = lazyWithRetry(() => import("@/pages/VendorDeals"));

// Components
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ScrollRestoration } from "@/components/ScrollRestoration";
import { PWAInstallPrompt } from "@/components/PWAInstallPrompt";
const Sidebar = lazyWithRetry(() => import("@/components/Sidebar").then(m => ({ default: m.Sidebar })));

// Context
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SiteContentProvider } from "@/context/SiteContentContext";
import { NotificationProvider } from "@/context/NotificationContext";
import { SidebarProvider } from "@/context/SidebarContext";

import { BACKEND_URL } from "@/config/runtime";
import { trackPageView, initSessionTracking } from "@/utils/analytics";

// Initialize session tracking once on app load
initSessionTracking();

// Loading fallback for lazy-loaded components
const PageLoader = () => (
  <div className="min-h-[50vh] flex items-center justify-center">
    <div className="w-10 h-10 border-4 border-[#2D6A4F] border-t-transparent rounded-full animate-spin"></div>
  </div>
);

// Page View Tracker - tracks page views for admin analytics
const PageViewTracker = () => {
  const location = useLocation();
  
  useEffect(() => {
    // Track page view on route change
    trackPageView(location.pathname, document.title);
  }, [location.pathname]);
  
  return null;
};

// Google OAuth Callback Handler - handles redirect from Google OAuth
const GoogleAuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);
  const { login } = useAuth();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const params = new URLSearchParams(location.search);
      const authSuccess = params.get("auth");
      const error = params.get("error");

      if (error) {
        console.error("Auth error:", error);
        navigate("/login?error=" + error, { replace: true });
        return;
      }

      if (authSuccess === "success") {
        // Cookie was already set by backend, just fetch user data
        try {
          const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
            credentials: "include",
          });

          if (!response.ok) throw new Error("Failed to get user");

          const user = await response.json();
          login(user);
          
          // Navigate to dashboard without query params
          // Use window.location for a clean redirect to avoid React Router state issues
          const targetPath = user.account_type === "vendor" ? "/vendor/dashboard" : "/dashboard";
          window.location.replace(targetPath);
        } catch (error) {
          console.error("Auth error:", error);
          navigate("/login", { replace: true });
        }
      } else {
        // No auth param, redirect to login
        navigate("/login", { replace: true });
      }
    };

    processAuth();
  }, [navigate, login, location]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F9F5]">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-[#2D6A4F] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-[#4A6855] font-medium">Signing you in...</p>
      </div>
    </div>
  );
};

// Legacy Emergent Auth Callback (keeping for backwards compatibility)
const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const { login } = useAuth();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionId = new URLSearchParams(hash.substring(1)).get("session_id");

      if (!sessionId) {
        navigate("/login");
        return;
      }

      try {
        const response = await fetch(`${BACKEND_URL}/api/auth/session`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ session_id: sessionId }),
          credentials: "include",
        });

        if (!response.ok) throw new Error("Auth failed");

        const user = await response.json();
        login(user);
        
        // Redirect based on account type
        if (user.account_type === "vendor") {
          navigate("/vendor/dashboard", { state: { user } });
        } else {
          navigate("/dashboard", { state: { user } });
        }
      } catch (error) {
        console.error("Auth error:", error);
        navigate("/login");
      }
    };

    processAuth();
  }, [navigate, login]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F9F5]">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-[#2D6A4F] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-[#4A6855] font-medium">Signing you in...</p>
      </div>
    </div>
  );
};

const AuthGatedPage = ({ children }) => {
  const { isAuthenticated, loading, login } = useAuth();
  const [showGate, setShowGate] = useState(false);
  const [form, setForm] = useState({ email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (!isAuthenticated) {
      const timer = setTimeout(() => setShowGate(true), 2000);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, loading]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (res.ok && data.token) {
        localStorage.setItem("token", data.token);
        window.location.reload();
      } else {
        setError(data.detail || "Invalid credentials");
      }
    } catch {
      setError("Something went wrong");
    }
    setSubmitting(false);
  };

  return (
    <div className="relative">
      {children}
      {showGate && !isAuthenticated && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ animation: "fadeIn 0.4s ease" }}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div className="relative bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md mx-4" data-testid="auth-gate-modal">
            <div className="text-center mb-6">
              <img src="/logo-80.webp" alt="CampYid" className="w-12 h-12 mx-auto mb-3 rounded-xl" />
              <h2 className="text-2xl font-bold text-[#1E3A2F]">Sign in to continue</h2>
              <p className="text-[#2D5A3D]/70 text-sm mt-1">Please sign in to access this feature</p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && <p className="text-red-600 text-sm text-center bg-red-50 rounded-lg p-2">{error}</p>}
              <div>
                <label className="block text-sm font-medium text-[#1E3A2F] mb-1">Email</label>
                <input
                  type="email"
                  required
                  className="w-full rounded-lg border border-stone-300 px-4 py-2.5 text-[#1E3A2F] focus:outline-none focus:ring-2 focus:ring-[#2D5A3D]/30 focus:border-[#2D5A3D]"
                  value={form.email}
                  onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                  data-testid="auth-gate-email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1E3A2F] mb-1">Password</label>
                <input
                  type="password"
                  required
                  className="w-full rounded-lg border border-stone-300 px-4 py-2.5 text-[#1E3A2F] focus:outline-none focus:ring-2 focus:ring-[#2D5A3D]/30 focus:border-[#2D5A3D]"
                  value={form.password}
                  onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))}
                  data-testid="auth-gate-password"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#1E3A2F] text-white font-bold py-3 rounded-full hover:bg-[#2D5A3D] transition-colors disabled:opacity-50"
                data-testid="auth-gate-submit"
              >
                {submitting ? "Signing in..." : "Sign In"}
              </button>
            </form>
            <p className="text-center text-sm text-[#2D5A3D]/60 mt-4">
              Don't have an account?{" "}
              <span className="text-[#1E3A2F] font-semibold underline cursor-pointer" onClick={() => navigate("/login?tab=register")}>
                Register here
              </span>
            </p>
          </div>
          <style>{`@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }`}</style>
        </div>
      )}
    </div>
  );
};

const ProtectedRoute = ({ children, requiredType = null }) => {
  const { user, isAuthenticated, loading, checkAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (location.state?.user) {
      setChecked(true);
      return;
    }

    const verify = async () => {
      const authenticated = await checkAuth();
      if (!authenticated) {
        navigate("/login", { state: { from: location } });
      }
      setChecked(true);
    };

    if (!loading) {
      verify();
    }
  }, [location, navigate, checkAuth, loading]);

  if (loading || !checked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9F5]">
        <div className="w-12 h-12 border-4 border-[#2D6A4F] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Will be redirected by useEffect above
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9F5]">
        <div className="w-12 h-12 border-4 border-[#2D6A4F] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Check if user is admin (either by account_type or is_admin flag)
  const isAdminUser = user?.account_type === "admin" || user?.is_admin === true;
  
  // Admin users should only access admin routes and settings
  if (isAdminUser && user?.account_type === "admin") {
    const adminPaths = ["/admin", "/settings"];
    const isAdminPath = adminPaths.some(p => location.pathname.startsWith(p));
    if (!isAdminPath) {
      navigate("/admin");
      return null;
    }
  }

  if (requiredType && user?.account_type !== requiredType) {
    // Handle admin required type - check both account_type and is_admin flag
    if (requiredType === "admin" && isAdminUser) {
      // User is admin (either by type or flag), allow access
      return children;
    }
    // Redirect based on account type
    if (isAdminUser && user?.account_type === "admin") {
      navigate("/admin");
    } else if (user?.account_type === "vendor") {
      navigate("/vendor/dashboard");
    } else {
      navigate("/dashboard");
    }
    return null;
  }

  return children;
};

const AppRouter = () => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  // Pages where sidebar should NOT be shown (even when authenticated)
  const noSidebarPaths = ["/login", "/register", "/forgot-password", "/reset-password", "/payment/success", "/payment/cancel", "/payment/embedded-complete"];
  const showSidebar = isAuthenticated && !noSidebarPaths.some(path => location.pathname === path);
  
  // Show navbar on all routes (including admin) for consistency with vendor/camp dashboards
  const showNavbar = true;

  // Check for session_id in URL fragment (legacy Emergent Auth)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  // Check for auth=success in query params (new Google OAuth)
  if (location.search?.includes("auth=success")) {
    return <GoogleAuthCallback />;
  }

  return (
    <div className="min-h-screen flex bg-white">
      {showSidebar && <Suspense fallback={null}><Sidebar /></Suspense>}
      <PageViewTracker />
      <div className="flex-1 flex flex-col min-h-screen min-w-0">
        <ScrollRestoration />
        {showNavbar && <Navbar />}
        <main className="flex-1">
          <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/category/:slug" element={<SearchPage />} />
          <Route path="/listing/:slug" element={<ListingPage />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/auth/google/callback" element={<GoogleAuthCallback />} />
          <Route
            path="/dashboard"
            element={<CampDashboard />}
          />
          <Route
            path="/dashboard/calendar"
            element={
              <ProtectedRoute>
                <CampCalendar />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/schedule-designer"
            element={
              <ProtectedRoute>
                <ScheduleDesigner />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/teams"
            element={
              <ProtectedRoute>
                <TeamsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/teams/:teamId"
            element={
              <ProtectedRoute>
                <TeamsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/teams/join"
            element={<TeamsPage />}
          />
          <Route
            path="/dashboard/reminders"
            element={
              <ProtectedRoute>
                <RemindersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/kanban"
            element={
              <ProtectedRoute>
                <KanbanPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/kanban/:boardId"
            element={
              <ProtectedRoute>
                <KanbanPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/todos"
            element={
              <ProtectedRoute>
                <KanbanPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/trips"
            element={
              <AuthGatedPage>
                <TripBoard />
              </AuthGatedPage>
            }
          />
          <Route
            path="/trips/post"
            element={
              <ProtectedRoute>
                <PostTrip />
              </ProtectedRoute>
            }
          />
          <Route path="/deals" element={<Navigate to="/search?deals=true" replace />} />
          <Route
            path="/vendor/deals"
            element={
              <ProtectedRoute requiredType="vendor">
                <VendorDeals />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vendor/dashboard"
            element={
              <ProtectedRoute requiredType="vendor">
                <VendorDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vendor/listings/new"
            element={
              <ProtectedRoute requiredType="vendor">
                <CreateListing />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vendor/listings/:id/edit"
            element={
              <ProtectedRoute requiredType="vendor">
                <CreateListing />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <AccountSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/notifications"
            element={
              <ProtectedRoute>
                <NotificationSettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute requiredType="admin">
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/reject-listing"
            element={<RejectListingPage />}
          />
          <Route
            path="/admin/approval-success"
            element={<ApprovalSuccessPage />}
          />
          <Route
            path="/admin/edit-listing/:id"
            element={
              <ProtectedRoute requiredType="admin">
                <CreateListing />
              </ProtectedRoute>
            }
          />
          <Route path="/pricing" element={<PricingPage />} />
          <Route
            path="/payment/success"
            element={<PaymentSuccess />}
          />
          <Route
            path="/payment/embedded-complete"
            element={<PaymentSuccess embedded={true} />}
          />
          <Route
            path="/payment/cancel"
            element={<PaymentCancel />}
          />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/for-vendors" element={<ForVendorsPage />} />
          <Route path="/for-directors" element={<ForDirectorsPage />} />
          <Route path="/unsubscribe/:token" element={<UnsubscribePage />} />
        </Routes>
          </Suspense>
      </main>
      <Footer />
      <Toaster position="bottom-right" richColors />
      <PWAInstallPrompt />
      </div>
    </div>
  );
};

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <NotificationProvider>
            <SiteContentProvider>
              <SidebarProvider>
                <AppRouter />
              </SidebarProvider>
            </SiteContentProvider>
          </NotificationProvider>
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
